import { isSeoUserAgentFromNavigatorGlob } from '@bcf-vanilla-shared-ts-v1/seo/is-seo-user-agent';
import { delay, first, of, type Observable } from 'rxjs';
import type { LitswRegistrationConfig } from './litsw-registration.types';

let registrationStarted: boolean = false;
let controllerListenerAttached: boolean = false;

export function registerLitswWorker(config: LitswRegistrationConfig, lang?: string): void {
  if (registrationStarted) {
    return;
  }
  if (isSeoUserAgentFromNavigatorGlob()) {
    return;
  }
  if (!('serviceWorker' in navigator && config.enabled !== false)) {
    return;
  }

  registrationStarted = true;
  attachControllerListener();

  resolveRegistrationStrategy(config.registrationStrategy)
    .pipe(first())
    .subscribe(() => {
      performLitswRegistration(config, lang);
    });
}

function performLitswRegistration(config: LitswRegistrationConfig, lang?: string): void {
  const script: string = config.withLangQuery && lang ? `${config.script}?lang=${lang}&p0=a0.js` : config.script;

  navigator.serviceWorker
    .register(script, { scope: config.scope })
    .catch((err: unknown) => console.error('Service worker registration failed with:', err));
}

function resolveRegistrationStrategy(
  registrationStrategy: LitswRegistrationConfig['registrationStrategy']
): Observable<unknown> {
  if (typeof registrationStrategy === 'function') {
    return registrationStrategy();
  }

  // eslint-disable-next-line @typescript-eslint/typedef
  const [strategy, ...args] = (registrationStrategy || 'registerWhenStable:3000').split(':');

  switch (strategy) {
    case 'registerImmediately':
      return of(null);
    case 'registerWithDelay':
      return delayWithTimeout(+args[0] || 0);
    case 'registerWhenStable':
      return delayWithTimeout(+args[0] || 0);
    default:
      throw new Error(`Unknown ServiceWorker registration strategy: ${registrationStrategy}`);
  }
}

function attachControllerListener(): void {
  if (controllerListenerAttached) {
    return;
  }
  controllerListenerAttached = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (navigator.serviceWorker.controller !== null) {
      navigator.serviceWorker.controller.postMessage({ action: 'INITIALIZE' });
    }
  });
}

function delayWithTimeout(timeout: number): Observable<unknown> {
  return of(null).pipe(delay(timeout));
}
